import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Download, Shield, Check, Copy } from "lucide-react";
import { toast } from "sonner";

export default function RecoveryCodesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const codes = location.state?.recovery_codes || [];
  const email = location.state?.email || "";

  const [downloaded, setDownloaded] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // If no codes in state, user navigated here directly — redirect
  if (!codes.length) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-6">
        <div className="text-center space-y-3">
          <p className="text-sm text-[#555555]">No recovery codes to display.</p>
          <Button onClick={() => navigate("/inbox")} variant="outline" size="sm" data-testid="recovery-go-inbox">Go to inbox</Button>
        </div>
      </div>
    );
  }

  const handleDownload = () => {
    const now = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    const content = [
      "QuickInbox Account Recovery",
      "",
      `Email: ${email}`,
      "",
      "IMPORTANT:",
      "Store this file securely. These recovery codes allow access to your account.",
      "Each code can only be used once.",
      "",
      "Recovery Codes:",
      "",
      ...codes.map((c, i) => `  ${String(i + 1).padStart(2, " ")}. ${c}`),
      "",
      `Generated on: ${new Date().toLocaleString()}`,
      "",
      "Do NOT share this file.",
    ].join("\n");

    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `quickinbox-recovery-codes-${now}.txt`;
    a.click();
    URL.revokeObjectURL(url);

    setDownloaded(true);
    toast.success("Recovery file downloaded");
  };

  const handleCopyAll = () => {
    navigator.clipboard.writeText(codes.join("\n")).then(() => {
      toast.success("Codes copied to clipboard");
    }).catch(() => toast.error("Copy failed"));
  };

  const handleContinue = async () => {
    setSubmitting(true);
    try {
      await api.post("/auth/confirm-recovery-codes");
      navigate("/inbox");
    } catch {
      toast.error("Failed to confirm. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const canContinue = downloaded && confirmed;

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-6 py-12" data-testid="recovery-codes-page">
      <div className="w-full max-w-md space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="w-12 h-12 mx-auto rounded-xl bg-zinc-900 flex items-center justify-center">
            <Shield size={22} className="text-white" />
          </div>
          <h1 className="font-heading text-2xl font-medium text-[#111111]" data-testid="recovery-title">
            Save your recovery codes
          </h1>
          <p className="text-sm text-zinc-500">
            These codes are required to recover your account. Store them safely. Each code can be used once.
          </p>
        </div>

        {/* Codes grid */}
        <div className="bg-[#F5F5F5] border border-[#E5E5E5] rounded-lg p-4" data-testid="recovery-codes-list">
          <div className="grid grid-cols-2 gap-2">
            {codes.map((code, i) => (
              <div key={i} className="flex items-center gap-2 px-3 py-1.5 bg-white rounded border border-[#E5E5E5]">
                <span className="text-[10px] text-zinc-500 font-mono w-5">{i + 1}.</span>
                <code className="text-sm font-mono font-medium text-[#555555]" data-testid={`recovery-code-${i}`}>{code}</code>
              </div>
            ))}
          </div>
          <button
            onClick={handleCopyAll}
            className="mt-3 flex items-center gap-1.5 text-xs text-zinc-500 hover:text-[#555555] transition-colors"
            data-testid="recovery-copy-all"
          >
            <Copy size={12} /> Copy all codes
          </button>
        </div>

        {/* Download button */}
        <Button
          onClick={handleDownload}
          className="w-full h-11 bg-zinc-900 hover:bg-zinc-800 text-white font-medium"
          data-testid="recovery-download-button"
        >
          <Download size={16} className="mr-2" />
          {downloaded ? "Downloaded" : "Download recovery file (.txt)"}
          {downloaded && <Check size={14} className="ml-2 text-emerald-600" />}
        </Button>

        {/* Confirmation checkbox */}
        <div className="flex items-start gap-3 p-3 rounded-lg border border-[#E5E5E5]">
          <Checkbox
            id="confirm-saved"
            checked={confirmed}
            onCheckedChange={setConfirmed}
            disabled={!downloaded}
            data-testid="recovery-confirm-checkbox"
          />
          <label
            htmlFor="confirm-saved"
            className={`text-sm cursor-pointer select-none ${downloaded ? "text-[#555555]" : "text-[#999999]"}`}
          >
            I have saved my recovery codes in a safe place
          </label>
        </div>

        {/* Continue button */}
        <Button
          onClick={handleContinue}
          disabled={!canContinue || submitting}
          className="w-full h-11 font-medium"
          variant={canContinue ? "default" : "outline"}
          data-testid="recovery-continue-button"
        >
          {submitting ? "Confirming..." : "Continue to inbox"}
        </Button>

        {!canContinue && (
          <p className="text-center text-xs text-zinc-500">
            {!downloaded ? "Download your recovery file first" : "Check the confirmation box to continue"}
          </p>
        )}
      </div>
    </div>
  );
}
